import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listAdminReportsApi, deleteAdminReportApi } from "api/admin";
import {
  C,
  Panel,
  SkeletonRows,
  Empty,
  SearchBar,
  Pagination,
  formatDate,
  dangerBtnSm,
} from "./admin_ui";

export default function AdminReports() {
  const [params, setParams] = useSearchParams();
  const search = params.get("q") || "";
  const page = parseInt(params.get("page") || "1", 10) || 1;
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0, top: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => setSearchInput(search), [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await listAdminReportsApi({
        search: search || undefined,
        page,
        limit: 20,
      });
      if (cancelled) return;
      if (r?.success)
        setData({
          items: r.items || [],
          total: r.total || 0,
          top: r.top || [],
        });
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [search, page, tick]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: false });
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("이 신고 내역을 삭제하시겠습니까?")) return;
    const r = await deleteAdminReportApi(docId);
    if (r?.success) setTick((t) => t + 1);
    else alert(r?.error || "삭제 실패");
  };

  return (
    <>
      <div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 800,
            color: C.ink,
            letterSpacing: "-0.02em",
          }}
        >
          신고 관리
        </div>
        <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
          전체 {data.total.toLocaleString()}건 — 신고당한 회원의 누적 횟수를 우측에서 확인하세요.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1 }} />
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              onSubmit={() => setParam("q", searchInput.trim())}
              placeholder="피신고자 / 신고자 / 사유"
            />
          </div>
          <Panel title="신고 내역" count={data.total}>
            {loading ? (
              <SkeletonRows count={6} />
            ) : error ? (
              <Empty label={error} />
            ) : data.items.length === 0 ? (
              <Empty />
            ) : (
              data.items.map((r) => (
                <div
                  key={r.docId}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "12px 0",
                    borderBottom: `1px solid ${C.line}`,
                    fontSize: 13,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: C.sub,
                        marginBottom: 4,
                        fontWeight: 600,
                      }}
                    >
                      {r.name || r.id} ← {r.reportedBy} · {formatDate(r.reportDate)}
                    </div>
                    <div
                      style={{
                        color: C.ink,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {r.reason || "—"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(r.docId)}
                    style={{ ...dangerBtnSm, height: 30, fontSize: 11 }}
                  >
                    삭제
                  </button>
                </div>
              ))
            )}
            <Pagination
              page={page}
              limit={20}
              total={data.total}
              onChange={(p) => setParam("page", String(p))}
            />
          </Panel>
        </div>

        <Panel title="신고 누적 회원" count={data.top?.length ?? 0}>
          {loading ? (
            <SkeletonRows count={5} />
          ) : data.top?.length === 0 ? (
            <Empty label="누적 신고가 없습니다." />
          ) : (
            (data.top || []).map((t) => (
              <div
                key={t.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 0",
                  borderBottom: `1px solid ${C.line}`,
                  fontSize: 13,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    color: C.ink,
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.name || t.id}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color:
                      t.count >= 5
                        ? C.red700
                        : t.count >= 3
                        ? C.amber800
                        : C.greenDark,
                    background:
                      t.count >= 5
                        ? C.red50
                        : t.count >= 3
                        ? C.amber50
                        : C.greenBg,
                    padding: "3px 10px",
                    borderRadius: 9999,
                  }}
                >
                  {t.count}회
                </div>
              </div>
            ))
          )}
        </Panel>
      </div>
    </>
  );
}
